import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Save, PanelRightClose, PanelRightOpen, Download, Printer, X } from 'lucide-react';
import type { JSONContent } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import { DossierEditor } from '../components/editor';
import { MeetingTasksSidebar } from '../components/MeetingTasksSidebar';
import { MeetingInfoSidebar } from '../components/MeetingInfoSidebar';
import { useRightPanel } from '../contexts/RightPanelContext';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { apiGet, apiPatch } from '../lib/api';
import { tiptapToMarkdown, downloadMarkdown, printAsPdf } from '../lib/export';

interface MeetingInstanceDetail {
  id: string;
  series_id: string | null;
  series_name: string | null;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  note_content: string | null;
  summary: string | null;
  attendee_ids: string[];
  default_attendee_ids?: string[];
  prev_instance_id: string | null;
  next_instance_id: string | null;
  created_at: string;
  updated_at: string;
}

export function MeetingInstancePage() {
  const { seriesId, instanceId } = useParams<{ seriesId?: string; instanceId: string }>();
  const [instance, setInstance] = useState<MeetingInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const { isOpen: sidebarOpen, setIsOpen: setSidebarOpen, register, unregister } = useRightPanel();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [taskRefreshTrigger, setTaskRefreshTrigger] = useState(0);
  const editorInstanceRef = useRef<Editor | null>(null);

  const handleEditorRef = useCallback((editor: Editor | null) => {
    editorInstanceRef.current = editor;
  }, []);

  /** When a task checkbox is toggled in the editor NodeView, refresh sidebar */
  const handleEditorTaskStatusChange = useCallback((_taskId: string, _newStatus: string) => {
    setTaskRefreshTrigger(t => t + 1);
  }, []);

  /** When a task is toggled in the sidebar, update the editor checkbox */
  const handleSidebarTaskStatusToggled = useCallback((taskId: string, newStatus: string) => {
    const editor = editorInstanceRef.current;
    if (!editor) return;
    const checked = newStatus === 'done';
    const { doc, tr } = editor.state;
    doc.descendants((node, pos) => {
      if (node.type.name === 'taskReference' && node.attrs.id === taskId) {
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, checked });
      }
    });
    if (tr.docChanged) {
      editor.view.dispatch(tr);
    }
    setTaskRefreshTrigger(t => t + 1);
  }, []);

  // Register this page as having a right panel
  useEffect(() => {
    register();
    return () => unregister();
  }, [register, unregister]);

  async function load() {
    if (!instanceId) return;
    setLoading(true);
    try {
      const data = await apiGet<MeetingInstanceDetail>(`/meeting-instances/${instanceId}`);
      setInstance(data);
    } catch (err) {
      console.error('Failed to load meeting instance:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [instanceId]);

  const handleNoteUpdate = useCallback(async (content: JSONContent) => {
    if (!instanceId) return;
    setSaving(true);
    try {
      await apiPatch(`/meeting-instances/${instanceId}`, {
        note_content: JSON.stringify(content),
      });
      setLastSaved(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Failed to save notes:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  }, [instanceId]);

  const handleAttendeeIdsChange = useCallback((newIds: string[]) => {
    setInstance(prev => prev ? { ...prev, attendee_ids: newIds } : prev);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  if (!instance) {
    return <div className="text-text-muted text-center py-16">Meeting not found.</div>;
  }

  const noteContent: JSONContent | null = instance.note_content
    ? (() => { try { return JSON.parse(instance.note_content); } catch { return null; } })()
    : null;

  const isStandalone = !instance.series_id;
  const displayTitle = instance.title || instance.series_name || 'Untitled Meeting';

  return (
    <div className="flex h-full relative">
      {/* Main content area */}
      <div className="flex-1 min-w-0 overflow-auto px-3 py-2 sm:p-6">
        <div>
          {/* Header */}
          <div className="mb-4 no-print">
            {/* Top row: back link | prev/next + actions (mobile) OR back | title | nav (desktop) */}
            <div className="flex items-center">
              {/* Left: back link */}
              <div className="flex-shrink-0">
                <Link
                  to={isStandalone ? '/meetings' : `/meetings/${seriesId}`}
                  className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
                >
                  <ChevronLeft size={16} />
                  {isStandalone ? 'Meetings' : instance.series_name}
                </Link>
              </div>

              {/* Center: title + date — hidden on mobile, shown on desktop */}
              <div className="hidden sm:block flex-1 min-w-0 text-center">
                <h1 className="text-lg font-bold text-text-primary truncate">
                  {displayTitle}
                </h1>
                <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(instance.meeting_date)}
                  </span>
                </div>
              </div>

              {/* Right: prev/next (only for series) + save status + export/print */}
              <div className="flex items-center gap-1 flex-shrink-0 ml-auto sm:ml-0">
              {!isStandalone && (
                <>
                  {instance.prev_instance_id ? (
                    <Link
                      to={`/meetings/${seriesId}/${instance.prev_instance_id}`}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                    >
                      <ChevronLeft size={14} /> Prev
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted/40">
                      <ChevronLeft size={14} /> Prev
                    </span>
                  )}
                  {instance.next_instance_id ? (
                    <Link
                      to={`/meetings/${seriesId}/${instance.next_instance_id}`}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                    >
                      Next <ChevronRight size={14} />
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted/40">
                      Next <ChevronRight size={14} />
                    </span>
                  )}
                  <div className="w-px h-4 bg-border mx-1" />
                </>
              )}
              {saving && <span className="text-xs text-accent mr-1">Saving...</span>}
              {lastSaved && !saving && (
                <span className="hidden sm:flex items-center gap-1 text-xs text-text-muted mr-1">
                  <Save size={10} />
                  {lastSaved}
                </span>
              )}
              <button
                onClick={() => {
                  if (noteContent) {
                    const md = tiptapToMarkdown(noteContent);
                    const filename = displayTitle.replace(/[^a-zA-Z0-9\s-]/g, '').trim();
                    downloadMarkdown(md, `${filename} - ${instance.meeting_date}`);
                  }
                }}
                className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                title="Export as Markdown"
              >
                <Download size={14} />
              </button>
              <button
                onClick={printAsPdf}
                className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                title="Print / Save as PDF"
              >
                <Printer size={14} />
              </button>
            </div>
          </div>

            {/* Mobile-only: title + date below the nav row */}
            <div className="sm:hidden text-center mt-2">
              <h1 className="text-lg font-bold text-text-primary truncate">
                {displayTitle}
              </h1>
              <div className="flex items-center justify-center gap-2 text-xs text-text-muted">
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(instance.meeting_date)}
                </span>
              </div>
            </div>
          </div>

          {/* Editor */}
          <DossierEditor
            content={noteContent}
            onUpdate={handleNoteUpdate}
            editable={true}
            placeholder="Start taking notes for this meeting..."
            meetingInstanceId={instanceId}
            onTaskCreated={() => setTaskRefreshTrigger(t => t + 1)}
            onTaskStatusChange={handleEditorTaskStatusChange}
            editorRef={handleEditorRef}
          />
        </div>
      </div>

      {/* Edge tab / FAB to reopen right sidebar */}
      {/* Desktop: edge tab */}
      <button
        onClick={() => setSidebarOpen(true)}
        className={`hidden sm:flex absolute right-0 top-1 z-10 items-center justify-center w-6 h-8 bg-bg-secondary border border-r-0 border-border rounded-l-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-all duration-200 no-print ${
          sidebarOpen ? 'translate-x-full opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
        }`}
        title="Open sidebar"
      >
        <PanelRightOpen size={14} />
      </button>
      {/* Mobile: floating action button */}
      {!sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed bottom-6 right-6 z-10 flex sm:hidden items-center justify-center w-12 h-12 rounded-full bg-accent text-white shadow-lg hover:bg-accent-hover transition-colors no-print"
          title="Open sidebar"
        >
          <PanelRightOpen size={20} />
        </button>
      )}

      {/* Mobile backdrop for right panel */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black/50 sm:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Right sidebar — split into Tasks (top) and Info (bottom) */}
      <div
        className={`no-print ${
          isMobile
            ? `fixed inset-0 z-50 bg-bg-secondary transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`
            : `flex-shrink-0 border-l border-border bg-bg-secondary transition-all duration-200 ${sidebarOpen ? 'w-[280px]' : 'w-0 overflow-hidden'}`
        }`}
      >
        <div className={`${isMobile ? 'w-full' : 'w-[280px]'} h-full flex flex-col`}>
          {/* Close sidebar button */}
          <div className="flex items-center justify-between px-2 py-1 border-b border-border">
            <span className="text-sm font-medium text-text-secondary sm:hidden px-2">Meeting Details</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
              title="Close sidebar"
            >
              <span className="hidden sm:block"><PanelRightClose size={16} /></span>
              <span className="sm:hidden"><X size={20} /></span>
            </button>
          </div>
          {/* Tasks — top half */}
          <div className="flex-1 min-h-0 border-b border-border overflow-y-auto">
            <MeetingTasksSidebar
              instanceId={instanceId!}
              seriesId={instance.series_id ?? undefined}
              refreshTrigger={taskRefreshTrigger}
              onTaskStatusToggled={handleSidebarTaskStatusToggled}
            />
          </div>

          {/* Info — bottom half (or full height for standalone) */}
          <div className={`${isStandalone ? 'flex-1' : 'flex-1'} min-h-0 overflow-y-auto`}>
            <MeetingInfoSidebar
              instanceId={instanceId!}
              seriesId={instance.series_id ?? undefined}
              attendeeIds={instance.attendee_ids}
              defaultAttendeeIds={instance.default_attendee_ids}
              onAttendeeIdsChange={handleAttendeeIdsChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}
